import React, { useCallback } from 'react';

import useScreen from '@lyearn/core/hooks/useScreen';
import { useTranslation } from '@lyearn/i18n';
import { CounterPill, Stack, Typography } from '@lyearn/ui';
import { Link } from '@/components/Link';
import * as User from '@/components/User';
import { UserFragment } from '@/graphql/fragments/generated/User';
import ShowMoreList from '@/modules/learner/components/ShowMoreList';
import { learnerWidgetStyles } from '@/modules/learner/views/LearnerProfile/styles';
import { LearnerWidgetProps } from '@/modules/learner/views/LearnerProfile/types';
import routes from '@/pages/corporate/routes';
import { useAdminRoute } from '@/pages/corporate/routes/hooks/useAdminRoutes';

import {
  LearnerProfileHierarchyWidgetFragment,
  useLearnerProfileHierarchyWidgetQuery,
} from '../../graphql/queries/generated/learnerProfileHierarchyWidget';
import CardLoader from '../LearnerGroupsWidget/CardLoader';

// TODO: Move out
export const renderReporteeUser = (user: UserFragment, isAdmin: boolean, isMobile: boolean) => (
  <Link to={isAdmin ? routes.AdminLearnerProfile(user.id) : routes.LearnerProfile(user.id)}>
    <User.Provider
      key={user.id}
      alignItems="center"
      className="group flex items-center space-s-12 xs:space-s-16"
      user={user}>
      <User.ProfilePicture size={isMobile ? 'xx-large' : 'medium'} />
      <Stack alignItems="start" className="flex-col" gapY={2} justifyContent="between">
        <User.Name
          className="group-hover:text-text-secondary"
          color="text-text-primary"
          numberOfLines={1}
          variant={isMobile ? 'label-01' : 'body-short-02'}
        />
        {isMobile ? <User.UniqueId color="text-text-secondary" variant="body-short-01" /> : null}
      </Stack>
    </User.Provider>
  </Link>
);

const LearnerHierarchy = (props: LearnerWidgetProps) => {
  const { learner: _learner } = props;
  const { t } = useTranslation();
  const isAdmin = useAdminRoute();
  const { isMobile } = useScreen();

  const [{ fetching, data, stale }] = useLearnerProfileHierarchyWidgetQuery({
    variables: { learnerId: _learner.id },
  });
  const learner = data?.user as LearnerProfileHierarchyWidgetFragment | undefined;

  const renderItem = useCallback(
    (user: UserFragment) => renderReporteeUser(user, isAdmin, isMobile),
    [isAdmin],
  );

  if ((fetching || stale) && !learner?.reportees) {
    return <CardLoader />;
  }

  if (!learner || !Boolean(learner.manager || learner.reportees?.length)) {
    return null;
  }

  const renderLearnerManager = () => {
    return learner.manager ? (
      <Stack direction="col" gapY={isMobile ? 24 : 16}>
        <Typography variant={isMobile ? 'heading-s' : 'heading-xs'}>
          {t('LearnerProfile:Manager')}
        </Typography>
        {renderReporteeUser(learner.manager, isAdmin, isMobile)}
      </Stack>
    ) : null;
  };

  const renderLearnerReportees = () => {
    const reportees = learner.reportees || [];

    return reportees.length ? (
      <Stack direction="col" gapY={isMobile ? 24 : 16}>
        <Stack alignItems="center" gapX={8}>
          <Typography variant={isMobile ? 'heading-s' : 'heading-xs'}>
            {t('LearnerProfile:Reportees')}
          </Typography>
          <CounterPill label={reportees.length} />
        </Stack>
        <ShowMoreList
          defaultShowCount={isMobile ? reportees.length : 4}
          items={reportees}
          renderItem={renderItem}
        />
      </Stack>
    ) : null;
  };

  return (
    <Stack className="flex-col xs:px-24 xs:mt-12 xs:w-full" gapY={isMobile ? 52 : 32}>
      {renderLearnerManager()}
      {renderLearnerReportees()}
    </Stack>
  );
};

export const LearnerHierarchyWidget = (props: LearnerWidgetProps) => {
  return (
    <Stack className={learnerWidgetStyles} direction="col" gapY={32}>
      <LearnerHierarchy {...props} />
    </Stack>
  );
};

export default LearnerHierarchy;
